// TalleresTable.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { Table, Input, Select, Alert, Modal, Form, DatePicker, App, Tooltip, Dropdown } from 'antd';
import { SearchOutlined, PlusOutlined, EditOutlined, ClockCircleOutlined, EnvironmentOutlined, MoreOutlined, StopOutlined, HistoryOutlined, PlayCircleOutlined, PauseCircleOutlined, CalendarOutlined, DownloadOutlined, AppstoreOutlined, ExclamationCircleOutlined, TagOutlined, LoadingOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import HorariosModal from './TalleresTableHorarios';
import useAuthContext from '../../../../hooks/useAuthContext';
import { can } from '../../../../config/rbac';
import CiclosService from '../../../../service/Ciclos';
import TalleresService from '../../../../service/Talleres';
import TextArea from 'antd/es/input/TextArea';
import { useWithNotify } from '../../../../providers/NotificationProvider';
import { exportarLibro } from '../../../../utils/exportExcel';
import { EncabezadoAdmin, TarjetaResumen, FiltroChips, claseBtnPrimario, claseBtnSecundario } from '../../../Ui/Admin/PiezasAdmin';
import { ListaTalleres, claseBtnPeligro, claseTituloSeccion } from './piezasTalleres';

const { RangePicker } = DatePicker;
const { Option } = Select;

const MOTIVOS_CIERRE = [
  { value: 'cierre_manual', label: 'Cierre manual' },
  { value: 'fecha_fin_alcanzada', label: 'Terminó el período de actividades' },
  { value: 'inicio_nuevo_ciclo', label: 'Se inicia un nuevo ciclo' },
  { value: 'otro', label: 'Otro' },
];

// Estado del periodo de actividades. Sirve para ver de un vistazo si el taller
// quedó con las fechas del ciclo anterior (ya finalizadas) o si todavía no parte.
const getEstadoActividades = record => {
  const inicio = record.actividadesInicio ? dayjs(record.actividadesInicio) : null;
  const fin = record.actividadesFin ? dayjs(record.actividadesFin) : null;
  if (!inicio && !fin) return null;

  const hoy = dayjs();
  if (fin && hoy.isAfter(fin, 'day')) {
    // Un taller activo con actividades ya terminadas es el caso que hay que corregir.
    return { texto: 'Finalizado', color: record.estado === 'activo' ? 'red' : 'default' };
  }
  if (inicio && hoy.isBefore(inicio, 'day')) return { texto: 'Por iniciar', color: 'blue' };
  return { texto: 'En curso', color: 'green' };
};

const TalleresTable = ({ talleres, onRefresh }) => {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [formCerrarIndividual] = Form.useForm();
  const [formNuevoCiclo] = Form.useForm();
  const [formCerrarMasivo] = Form.useForm();
  const [formActividadesFechas] = Form.useForm(); // 🆕 NUEVO FORM
  const withNotify = useWithNotify();
  const { modal } = App.useApp();

  // Filtros / estado UI
  const [searchText, setSearchText] = useState('');
  const [recintoFilter, setRecintoFilter] = useState('all');
  const [estadoFilter, setEstadoFilter] = useState('all');
  const [inscFilter, setInscFilter] = useState('all');
  const [horariosModalVisible, setHorariosModalVisible] = useState(false);
  const [selectedTaller, setSelectedTaller] = useState(null);

  // Ciclos
  const [cerrarCicloModalVisible, setCerrarCicloModalVisible] = useState(false);
  const [iniciarCicloModalVisible, setIniciarCicloModalVisible] = useState(false);
  const [cerrarMasivoVisible, setCerrarMasivoVisible] = useState(false);
  const [actividadesFechasVisible, setActividadesFechasVisible] = useState(false); // 🆕 NUEVO MODAL
  const [tallerParaCerrarCiclo, setTallerParaCerrarCiclo] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loadingCiclo, setLoadingCiclo] = useState(false);
  const [loadingBulk, setLoadingBulk] = useState(false);

  // Jefe recinto: la tabla se acota a SU recinto. Esto no es un permiso sino un
  // alcance de datos, y sigue viniendo del área asignada a la cuenta.
  const isJefeRecinto = user?.isJefe_recinto || false;
  const nombreRecintoDelJefe = user?.area;

  // Solo lectura: puede ver talleres e inscritos, pero no editarlos. Antes esto se
  // deducía de "es ventas y no es ninguno de los otros tres"; ahora es directamente
  // la ausencia del permiso de edición, que es lo que la condición quería decir.
  const isVentasOnly = !can(user, 'talleres', 'editar');

  // Utils actualizaciones locales
  const patchTaller = (arr, id, patch) => arr.map(t => (t._id === id ? { ...t, ...patch } : t));
  const incCiclo = (t) => ({ cicloActual: (t.cicloActual || 0) + 1 });

  // Talleres visibles
  const talleresVisibles = useMemo(() => {
    if (!isJefeRecinto || !nombreRecintoDelJefe) return talleres;
    return talleres?.filter(t => t.recinto?.nombre === nombreRecintoDelJefe) || [];
  }, [talleres, isJefeRecinto, nombreRecintoDelJefe]);

  useEffect(() => {
    if (isJefeRecinto && nombreRecintoDelJefe) setRecintoFilter(nombreRecintoDelJefe);
  }, [isJefeRecinto, nombreRecintoDelJefe]);

  // Filtro rápido de las tarjetas de resumen: lo que requiere atención.
  const [vista, setVista] = useState('todos'); // todos | pausados | sinCiclo | sinSku
  const tieneSinSku = t => (t.horarios || []).some(h => !h.sku);

  // Filtrados
  const q = searchText.trim().toLowerCase();
  const filteredData = talleresVisibles?.filter(taller => {
    const matchSearch = !q
      || taller.nombre.toLowerCase().includes(q)
      || (taller.descripcion || '').toLowerCase().includes(q)
      || (taller.recinto?.nombre || '').toLowerCase().includes(q);
    const matchRecinto = recintoFilter === 'all' || (taller.recinto && taller.recinto.nombre === recintoFilter);
    const matchState = estadoFilter === 'all' || taller.estado === estadoFilter;
    const matchInsc = inscFilter === 'all' || taller.inscripcionesEstado === inscFilter;
    const matchVista = vista === 'todos'
      || (vista === 'pausados' && taller.estado !== 'activo')
      || (vista === 'sinCiclo' && !taller.enCicloActivo)
      || (vista === 'sinSku' && tieneSinSku(taller));
    return matchSearch && matchRecinto && matchState && matchInsc && matchVista;
  }) || [];

  const conteo = useMemo(() => {
    const lista = talleresVisibles || [];
    return {
      total: lista.length,
      activos: lista.filter(t => t.estado === 'activo').length,
      pausados: lista.filter(t => t.estado !== 'activo').length,
      sinCiclo: lista.filter(t => !t.enCicloActivo).length,
      sinSku: lista.filter(tieneSinSku).length,
    };
  }, [talleresVisibles]);

  const elegirTarjeta = (clave) => {
    if (clave === 'activos') { setEstadoFilter('activo'); setVista('todos'); }
    else { setEstadoFilter('all'); setVista(clave); }
  };
  const tarjetaActiva = vista !== 'todos' ? vista
    : estadoFilter === 'activo' ? 'activos'
      : estadoFilter === 'all' ? 'todos' : null;
  const hayFiltros = q || (recintoFilter !== 'all' && !isJefeRecinto) || estadoFilter !== 'all' || inscFilter !== 'all' || vista !== 'todos';
  const limpiarFiltros = () => {
    setSearchText(''); setEstadoFilter('all'); setInscFilter('all'); setVista('todos');
    if (!isJefeRecinto) setRecintoFilter('all');
  };

  const recintos = [...new Set(talleresVisibles?.map(t => t.recinto?.nombre).filter(Boolean))];

  // Subconjuntos desde la selección
  const selectedForNuevo = useMemo(
    () => filteredData.filter(t => selectedRowKeys.includes(t._id) && !t.enCicloActivo),
    [filteredData, selectedRowKeys]
  );
  const selectedForCerrar = useMemo(
    () => filteredData.filter(t => selectedRowKeys.includes(t._id) && t.enCicloActivo),
    [filteredData, selectedRowKeys]
  );

  // Talleres seleccionados (para activar/pausar)
  const selectedTalleres = useMemo(
    () => filteredData.filter(t => selectedRowKeys.includes(t._id)),
    [filteredData, selectedRowKeys]
  );

  // Talleres pausados seleccionados
  const selectedPausados = useMemo(
    () => selectedTalleres.filter(t => t.estado === 'pausado' || t.estado === 'borrador'),
    [selectedTalleres]
  );

  // Talleres activos seleccionados
  const selectedActivos = useMemo(
    () => selectedTalleres.filter(t => t.estado === 'activo'),
    [selectedTalleres]
  );

  // Stats — fórmula: ocupados + reservados sobre el total de cupos
  const getCuposStats = (taller) => {
    const horarios = taller.horarios || [];
    const cuposTotales   = horarios.reduce((sum, h) => sum + (h.cupos || 0), 0);
    const cuposOcupados  = horarios.reduce((sum, h) => sum + (h.cuposOcupados  || 0), 0);
    const cuposReservados = horarios.reduce((sum, h) => sum + (h.cuposReservados || 0), 0);
    const cuposUsados    = cuposOcupados + cuposReservados;
    const porcentajeOcupado  = cuposTotales > 0 ? (cuposOcupados  / cuposTotales) * 100 : 0;
    const porcentajeTotal    = cuposTotales > 0 ? (cuposUsados    / cuposTotales) * 100 : 0;
    const sobrecupo = cuposOcupados > cuposTotales;
    return { cuposOcupados, cuposReservados, cuposUsados, cuposTotales, porcentajeOcupado, porcentajeTotal, sobrecupo };
  };

  // Handlers individuales
  const handleViewHorarios = (taller) => {
    setSelectedTaller(taller);
    setHorariosModalVisible(true);
  };
  const handleCloseModalHorarios = () => {
    setHorariosModalVisible(false);
    setSelectedTaller(null);
  };
  const handleHorariosChange = (newHorarios) => {
    setSelectedTaller(prev => ({ ...prev, horarios: newHorarios }));
    if (onRefresh) onRefresh();
  };
  const handleVerHistorial = (taller) => {
    navigate(`/admin/talleres/${taller._id}/ciclos-historial`);
  };

  // Activación masiva
  const handleBulkActivate = async () => {
    if (selectedPausados.length === 0) {
      modal.warning({
        title: 'Sin talleres pausados',
        content: 'Selecciona talleres en estado "pausado" para activarlos.'
      });
      return;
    }

    modal.confirm({
      title: `¿Activar ${selectedPausados.length} taller(es)?`,
      content: (
        <div className="space-y-2 mt-3">
          <Alert
            message="Se activarán los siguientes talleres"
            type="info"
            showIcon
            className="mb-2"
          />
          <div className="max-h-48 overflow-y-auto border rounded p-2">
            {selectedPausados.map(t => (
              <div key={t._id} className="text-sm py-1">
                • {t.nombre} <span className="text-gray-500">({t.recinto?.nombre})</span>
              </div>
            ))}
          </div>
        </div>
      ),
      okText: 'Confirmar',
      cancelText: 'Cancelar',
      onOk: async () => {
        setLoadingBulk(true);
        try {
          const tallerIds = selectedPausados.map(t => t._id);
          const resultado = await withNotify(
            TalleresService.BulkActivate(tallerIds),
            {
              loading: 'Activando talleres...',
              success: 'Operación completada',
              error: 'Error en la activación masiva'
            }
          );

          const successCount = resultado.success.length;
          const errorCount = resultado.errors.length;

          // Actualización local
          if (Array.isArray(talleres) && talleres.length > 0 && successCount > 0) {
            let list = talleres;
            resultado.success.forEach(r => {
              list = patchTaller(list, r.id, { estado: 'activo' });
            });
            if (typeof onRefresh === 'function') onRefresh(list);
          } else if (typeof onRefresh === 'function') {
            onRefresh();
          }

          // Mostrar resultados
          if (errorCount > 0) {
            modal.warning({
              title: 'Activación completada con errores',
              content: (
                <div>
                  <p className="mb-2">✅ {successCount} taller(es) activados correctamente</p>
                  <p className="mb-2">❌ {errorCount} taller(es) no pudieron ser activados:</p>
                  <div className="max-h-48 overflow-y-auto border rounded p-2 text-sm">
                    {resultado.data.errors.map((err, idx) => (
                      <div key={idx} className="text-red-600 py-1">• {err.message}</div>
                    ))}
                  </div>
                </div>
              )
            });
          } else {
            modal.success({
              title: 'Activación exitosa',
              content: `${successCount} taller(es) activados correctamente`
            });
          }

          setSelectedRowKeys([]);

        } catch (error) {
          console.error('Error en activación masiva:', error);
        } finally {
          setLoadingBulk(false);
        }
      }
    });
  };

  // Pausa masiva
  const handleBulkPause = async () => {
    if (selectedActivos.length === 0) {
      modal.warning({
        title: 'Sin talleres activos',
        content: 'Selecciona talleres en estado "activo" para pausarlos.'
      });
      return;
    }

    modal.confirm({
      title: `¿Pausar ${selectedActivos.length} taller(es)?`,
      content: (
        <div className="space-y-2 mt-3">
          <Alert
            message="Se pausarán los siguientes talleres"
            description="Las inscripciones también se pausarán automáticamente"
            type="warning"
            showIcon
            className="mb-2"
          />
          <div className="max-h-48 overflow-y-auto border rounded p-2">
            {selectedActivos.map(t => (
              <div key={t._id} className="text-sm py-1">
                • {t.nombre} <span className="text-gray-500">({t.recinto?.nombre})</span>
              </div>
            ))}
          </div>
        </div>
      ),
      okText: 'Confirmar',
      cancelText: 'Cancelar',
      okButtonProps: { danger: true },
      onOk: async () => {
        setLoadingBulk(true);
        try {
          const tallerIds = selectedActivos.map(t => t._id);
          const resultado = await withNotify(
            TalleresService.BulkPause(tallerIds),
            {
              loading: 'Pausando talleres...',
              success: 'Operación completada',
              error: 'Error en la pausa masiva'
            }
          );

          const successCount = resultado.success.length;
          const errorCount = resultado.errors.length;

          // Actualización local
          if (Array.isArray(talleres) && talleres.length > 0 && successCount > 0) {
            let list = talleres;
            resultado.success.forEach(r => {
              list = patchTaller(list, r.id, { estado: 'pausado', inscripcionesEstado: 'pausadas' });
            });
            if (typeof onRefresh === 'function') onRefresh(list);
          } else if (typeof onRefresh === 'function') {
            onRefresh();
          }

          // Mostrar resultados
          if (errorCount > 0) {
            modal.warning({
              title: 'Pausa completada con errores',
              content: (
                <div>
                  <p className="mb-2">✅ {successCount} taller(es) pausados correctamente</p>
                  <p className="mb-2">❌ {errorCount} taller(es) no pudieron ser pausados:</p>
                  <div className="max-h-48 overflow-y-auto border rounded p-2 text-sm">
                    {resultado.data.errors.map((err, idx) => (
                      <div key={idx} className="text-red-600 py-1">• {err.message}</div>
                    ))}
                  </div>
                </div>
              )
            });
          } else {
            modal.success({
              title: 'Pausa exitosa',
              content: `${successCount} taller(es) pausados correctamente`
            });
          }

          setSelectedRowKeys([]);

        } catch (error) {
          console.error('Error en pausa masiva:', error);
        } finally {
          setLoadingBulk(false);
        }
      }
    });
  };

  // 🆕 NUEVO: Actualización masiva de fechas de actividades
  const handleBulkUpdateActivityDates = () => {
    if (selectedTalleres.length === 0) {
      modal.warning({
        title: 'Sin talleres seleccionados',
        content: 'Selecciona al menos un taller para actualizar sus fechas de actividades.'
      });
      return;
    }
    setActividadesFechasVisible(true);
  };

  const handleConfirmarActividadesFechas = async (values) => {
    if (!values.fechasActividades || values.fechasActividades.length !== 2) {
      modal.warning({
        title: 'Fechas requeridas',
        content: 'Debes seleccionar tanto la fecha de inicio como la de fin de actividades.'
      });
      return;
    }

    const [fechaInicio, fechaFin] = values.fechasActividades;
    const tallerIds = selectedTalleres.map(t => t._id);

    setLoadingBulk(true);
    try {
      const resultado = await withNotify(
        TalleresService.BulkUpdateActivityDates(
          tallerIds,
          fechaInicio.toISOString(),
          fechaFin.toISOString()
        ),
        {
          loading: 'Actualizando fechas de actividades...',
          success: 'Fechas actualizadas correctamente',
          error: 'Error al actualizar fechas'
        }
      );

      const successCount = resultado.success?.length || 0;
      const errorCount = resultado.errors?.length || 0;

      // Actualización local
      if (Array.isArray(talleres) && talleres.length > 0 && successCount > 0) {
        let list = talleres;
        resultado.success.forEach(r => {
          list = patchTaller(list, r.id, {
            actividadesInicio: r.actividadesInicio,
            actividadesFin: r.actividadesFin
          });
        });
        if (typeof onRefresh === 'function') onRefresh(list);
      } else if (typeof onRefresh === 'function') {
        onRefresh();
      }

      // Mostrar resultados
      if (errorCount > 0) {
        modal.warning({
          title: 'Actualización completada con errores',
          content: (
            <div>
              <p className="mb-2">✅ {successCount} taller(es) actualizados correctamente</p>
              <p className="mb-2">❌ {errorCount} taller(es) con errores:</p>
              <div className="max-h-48 overflow-y-auto border rounded p-2 text-sm">
                {resultado.errors.map((err, idx) => (
                  <div key={idx} className="text-red-600 py-1">
                    • {err.nombre || err.id}: {err.message}
                  </div>
                ))}
              </div>
            </div>
          )
        });
      } else {
        modal.success({
          title: 'Actualización exitosa',
          content: `${successCount} taller(es) actualizados con las nuevas fechas de actividades`
        });
      }

      setActividadesFechasVisible(false);
      formActividadesFechas.resetFields();
      setSelectedRowKeys([]);

    } catch (error) {
      console.error('Error en actualización masiva de fechas:', error);
    } finally {
      setLoadingBulk(false);
    }
  };

  // Cerrar ciclo individual
  const handleCerrarCiclo = (taller) => {
    if (!taller.enCicloActivo) {
      modal.warning({ title: 'Ciclo no activo', content: `El taller "${taller.nombre}" no tiene un ciclo activo para cerrar.` });
      return;
    }
    setTallerParaCerrarCiclo(taller);
    setCerrarCicloModalVisible(true);
  };
  const handleConfirmarCerrarCiclo = async (values) => {
    setLoadingCiclo(true);
    try {
      await withNotify(
        CiclosService.CerrarCiclo(tallerParaCerrarCiclo._id, {
          motivoCierre: values.motivoCierre || 'cierre_manual',
          notas: values.notas || ''
        }),
        { loading: 'Cerrando ciclo...', success: 'Ciclo cerrado exitosamente', error: 'Error al cerrar ciclo' }
      );

      // Actualización local inmediata
      if (Array.isArray(talleres) && talleres.length > 0) {
        const id = tallerParaCerrarCiclo._id;
        const newList = patchTaller(talleres, id, { enCicloActivo: false });
        if (typeof onRefresh === 'function') onRefresh(newList);
      } else if (typeof onRefresh === 'function') {
        onRefresh();
      }

      setCerrarCicloModalVisible(false);
      formCerrarIndividual.resetFields();
      setTallerParaCerrarCiclo(null);
    } finally {
      setLoadingCiclo(false);
      window.location.reload();
    }
  };

  // 🆕 Nuevo ciclo desde selección CON HORA
  const abrirNuevoDesdeSeleccion = () => {
    if (selectedForNuevo.length === 0) {
      modal.info({ title: 'Sin selección válida', content: 'Selecciona talleres sin ciclo activo para iniciar nuevo ciclo.' });
      return;
    }
    setIniciarCicloModalVisible(true);
  };
  
  const handleConfirmarNuevoCiclo = async (values) => {
    const idsParaNuevo = selectedForNuevo.map(t => t._id);
    if (idsParaNuevo.length === 0) {
      modal.info({ title: 'Sin selección válida', content: 'Selecciona talleres sin ciclo activo para iniciar nuevo ciclo.' });
      return;
    }
    
    setLoadingCiclo(true);
    try {
      // 🆕 Convertir a ISO string (con hora si está presente)
      const datosNuevoCiclo = {
        inscripcionInicio: values.inscripcionInicio ? dayjs(values.inscripcionInicio).toISOString() : undefined,
        inscripcionFin: values.inscripcionFin ? dayjs(values.inscripcionFin).toISOString() : undefined,
      };

      console.log('📤 Datos de nuevo ciclo:', datosNuevoCiclo);
      
      const resultado = await withNotify(
        CiclosService.IniciarNuevoCiclo(idsParaNuevo, datosNuevoCiclo),
        { loading: 'Iniciando nuevo ciclo...', success: 'Operación completada', error: 'Error al iniciar ciclos' }
      );

      const resOk = resultado.success || [];
      const resErr = resultado.error || [];
      const successCount = resultado?.length || 0;
      const errorCount = resErr.length;

      // Actualización local
      if (Array.isArray(talleres) && talleres.length > 0 && successCount > 0) {
        let list = talleres;
        for (const r of resOk) {
          const id = r.tallerId || r.taller || r.id;
          const current = list.find(x => x._id === id) || {};
          const patch = { enCicloActivo: true, ...incCiclo(current) };
          list = patchTaller(list, id, patch);
        }
        if (typeof onRefresh === 'function') onRefresh(list);
      } else if (typeof onRefresh === 'function') {
        onRefresh();
      }

      if (successCount > 0) {
        modal.success({
          title: 'Ciclos iniciados exitosamente',
          content: `${successCount} taller(es) han iniciado nuevo ciclo.${errorCount > 0 ? ` Se presentaron ${errorCount} error(es).` : ''}`,
          onOk: () => {
            setIniciarCicloModalVisible(false);
            formNuevoCiclo.resetFields();
          }
        });
      } else {
        modal.error({
          title: 'Error al iniciar ciclos',
          content: resErr.map(e => `${e.tallerNombre || e.tallerId}: ${e.error}`).join('\n') || 'Ocurrió un error desconocido'
        });
      }
    } catch {
      // already notified
    } finally {
      setLoadingCiclo(false);
    }
  };

  // Cierre masivo desde selección
  const abrirCerrarMasivo = () => {
    if (selectedForCerrar.length === 0) {
      modal.info({ title: 'Sin selección válida', content: 'Selecciona talleres con ciclo activo para cerrar.' });
      return;
    }
    setCerrarMasivoVisible(true);
  };
  const handleConfirmarCerrarMasivo = async (values) => {
    const ids = selectedForCerrar.map(t => t._id);
    if (ids.length === 0) {
      modal.info({ title: 'Sin selección válida', content: 'Selecciona talleres con ciclo activo para cerrar.' });
      return;
    }
    setLoadingCiclo(true);
    try {
      const resultado = await withNotify(
        CiclosService.CerrarMasivo(ids, {
          motivoCierre: values.motivoCierre || 'cierre_manual',
          notas: values.notas || ''
        }),
        { loading: 'Cerrando ciclos...', success: 'Cierre masivo completado', error: 'Error en cierre masivo' }
      );

      const okArr = resultado?.data?.resultados || [];
      const errArr = resultado?.data?.errores || [];
      const ok = okArr.length;
      const bad = errArr.length;

      // Actualización local
      if (Array.isArray(talleres) && talleres.length > 0 && ok > 0) {
        let list = talleres;
        for (const r of okArr) {
          const id = r.tallerId || r.taller || r.id;
          list = patchTaller(list, id, { enCicloActivo: false });
        }
        if (typeof onRefresh === 'function') onRefresh(list);
      } else if (typeof onRefresh === 'function') {
        onRefresh();
      }

      modal.success({
        title: 'Resultado del cierre',
        content: `${ok} ciclo(s) cerrados correctamente.${bad ? ` ${bad} error(es).` : ''}`
      });

      setCerrarMasivoVisible(false);
      formCerrarMasivo.resetFields();
    } catch {
      // already notified
    } finally {
      setLoadingCiclo(false);
      window.location.reload();
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    getCheckboxProps: () => ({}),
  };

  const ESTADO_TALLER = {
    activo: { label: 'Activo', cls: 'bg-blue-50 text-primary', punto: 'bg-primary' },
    pausado: { label: 'Pausado', cls: 'bg-gray-100 text-gray-600', punto: 'bg-gray-400' },
    borrador: { label: 'Borrador', cls: 'bg-gray-100 text-gray-500', punto: 'bg-gray-300' },
    eliminado: { label: 'Eliminado', cls: 'bg-red-50 text-red-700', punto: 'bg-red-500' },
  };
  const TONO_ACTIVIDADES = {
    red: 'bg-red-50 text-red-700', blue: 'bg-blue-50 text-primary',
    green: 'bg-secondary/40 text-[#5c4300]', default: 'bg-gray-100 text-gray-500',
  };
  // Rango legible: "1–31 oct", "17 sept – 5 oct", "desde 17 sept", "hasta 31 oct".
  // El año solo aparece si no es el actual. Antes: "17/09/26 → —".
  const mesCorto = d => d.format('MMM').replace('.', '');
  const conAnio = d => (d.year() !== dayjs().year() ? ` ${d.year()}` : '');
  const rangoLegible = (ini, fin) => {
    const a = ini ? dayjs(ini) : null;
    const b = fin ? dayjs(fin) : null;
    if (a && b) {
      if (a.isSame(b, 'month')) return `${a.date()}–${b.date()} ${mesCorto(b)}${conAnio(b)}`;
      if (a.isSame(b, 'year')) return `${a.date()} ${mesCorto(a)} – ${b.date()} ${mesCorto(b)}${conAnio(b)}`;
      return `${a.date()} ${mesCorto(a)}${conAnio(a)} – ${b.date()} ${mesCorto(b)}${conAnio(b)}`;
    }
    if (a) return `desde ${a.date()} ${mesCorto(a)}${conAnio(a)}`;
    if (b) return `hasta ${b.date()} ${mesCorto(b)}${conAnio(b)}`;
    return null;
  };

  // Antes eran 10 columnas (Ciclo aparecía dos veces, Recinto aparte, fechas en tres
  // líneas y los SKU como etiquetas) y la tabla pedía scroll horizontal. Ahora 6.
  const columns = [
    {
      title: 'Taller',
      dataIndex: 'nombre',
      key: 'nombre',
      // Todas las columnas con ancho: así el espacio sobrante se reparte en vez de
      // irse entero a esta, que en pantallas anchas quedaba casi vacía.
      width: 340,
      sorter: (a, b) => a.nombre.localeCompare(b.nombre),
      render: (text, record) => (
        <div className="min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <span className="font-bold text-gray-900 truncate" title={text}>{text}</span>
            {record.enCicloActivo ? (
              <span className="flex-shrink-0 px-2 py-0.5 rounded-full bg-blue-50 text-primary text-[11px] font-bold">Ciclo {record.cicloActual}</span>
            ) : (
              <Tooltip title="Sin ciclo activo: selecciónalo y usa «Nuevo ciclo».">
                <span className="flex-shrink-0 px-2 py-0.5 rounded-full bg-secondary/50 text-[#5c4300] text-[11px] font-bold cursor-help">Sin ciclo</span>
              </Tooltip>
            )}
          </div>
          <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5 truncate">
            <EnvironmentOutlined className="text-gray-400" /> {record.recinto?.nombre || 'Sin recinto'}
          </div>
        </div>
      ),
    },
    {
      title: 'Estado',
      key: 'estado',
      width: 170,
      render: (_, record) => {
        const e = ESTADO_TALLER[record.estado] || { label: record.estado, cls: 'bg-gray-100 text-gray-600', punto: 'bg-gray-400' };
        const abiertas = record.inscripcionesEstado === 'abiertas';
        return (
          <div className="flex flex-col items-start gap-1">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold ${e.cls}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${e.punto}`} /> {e.label}
            </span>
            <span className={`text-[11px] font-semibold ${abiertas ? 'text-[#5c4300]' : 'text-gray-500'}`}>
              Inscripciones {abiertas ? 'abiertas' : (record.inscripcionesEstado || '—')}
            </span>
          </div>
        );
      },
    },
    {
      // Inscripción y actividades juntas: así se ve si el taller quedó con las
      // fechas del ciclo vigente o arrastra las del anterior.
      title: 'Fechas',
      key: 'fechas',
      width: 290,
      sorter: (a, b) => new Date(a.actividadesInicio || 0).getTime() - new Date(b.actividadesInicio || 0).getTime(),
      render: (_, record) => {
        const act = getEstadoActividades(record);
        const desfase = record.inscripcionFin && record.actividadesInicio
          && dayjs(record.inscripcionFin).isAfter(dayjs(record.actividadesInicio));
        const inscripcion = rangoLegible(record.inscripcionInicio, record.inscripcionFin);
        const actividades = rangoLegible(record.actividadesInicio, record.actividadesFin);
        const detalle = (i, f) => `${i ? dayjs(i).format('DD/MM/YYYY HH:mm') : 'sin inicio'} → ${f ? dayjs(f).format('DD/MM/YYYY HH:mm') : 'sin cierre'}`;
        // Etiqueta en columna fija y valor sin cortes: una línea por fecha.
        return (
          <div className="grid grid-cols-[82px_1fr] gap-x-2 gap-y-1 items-center text-xs whitespace-nowrap">
            <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400">Inscripción</span>
            {inscripcion ? (
              <Tooltip title={detalle(record.inscripcionInicio, record.inscripcionFin)}>
                <span className="text-gray-800 font-semibold cursor-help truncate">{inscripcion}</span>
              </Tooltip>
            ) : <span className="text-gray-400">Control manual</span>}

            <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400">Actividades</span>
            <span className="flex items-center gap-1.5 min-w-0">
              {act ? (
                <>
                  <Tooltip title={detalle(record.actividadesInicio, record.actividadesFin)}>
                    <span className="text-gray-800 font-semibold cursor-help truncate">{actividades}</span>
                  </Tooltip>
                  <span className={`flex-shrink-0 px-1.5 py-px rounded-full text-[10px] font-bold ${TONO_ACTIVIDADES[act.color] || TONO_ACTIVIDADES.default}`}>{act.texto}</span>
                </>
              ) : <span className="px-1.5 py-px rounded-full text-[10px] font-bold bg-red-50 text-red-700">Sin fechas</span>}
              {desfase && (
                <Tooltip title="La inscripción cierra después del inicio de actividades">
                  <span className="flex-shrink-0 px-1.5 py-px rounded-full text-[10px] font-bold bg-secondary/50 text-[#5c4300] cursor-help">Revisar</span>
                </Tooltip>
              )}
            </span>
          </div>
        );
      },
    },
    {
      title: 'Cupos',
      key: 'cupos',
      width: 170,
      sorter: (a, b) => getCuposStats(a).porcentajeTotal - getCuposStats(b).porcentajeTotal,
      render: (_, record) => {
        const { cuposOcupados, cuposReservados, cuposTotales, porcentajeOcupado, porcentajeTotal, sobrecupo } = getCuposStats(record);
        if (!cuposTotales) return <span className="text-xs text-gray-400">Sin cupos</span>;
        // Colores de texto con contraste AA sobre blanco (los claros de antd no llegaban).
        const tono = sobrecupo ? { txt: 'text-[#7f1d1d]', barra: 'bg-[#b91c1c]' }
          : porcentajeTotal >= 90 ? { txt: 'text-[#cf1322]', barra: 'bg-[#cf1322]' }
            : porcentajeTotal >= 60 ? { txt: 'text-[#874d00]', barra: 'bg-secondary' }
              : { txt: 'text-primary', barra: 'bg-primary' };
        return (
          <div className="w-full max-w-[150px]">
            <div className="flex items-baseline justify-between gap-2">
              <span className={`text-sm font-extrabold tabular-nums ${tono.txt}`}>{cuposOcupados}/{cuposTotales}</span>
              <span className={`text-[11px] font-semibold ${tono.txt}`}>{sobrecupo ? 'Sobrecupo' : `${porcentajeOcupado.toFixed(0)}%`}</span>
            </div>
            <div className="h-1.5 mt-1 rounded-full bg-gray-100 overflow-hidden" aria-hidden>
              <div className={`h-full rounded-full ${tono.barra}`} style={{ width: `${Math.min(porcentajeTotal, 100)}%` }} />
            </div>
            {cuposReservados > 0 && <div className="text-[11px] text-[#92400e] mt-0.5">{cuposReservados} en reserva</div>}
          </div>
        );
      },
    },
    {
      // El SKU amarra el horario con Bsale: sin él no se emite la boleta. Se muestra
      // el faltante; la lista completa queda en el tooltip y en el modal.
      title: 'Horarios',
      key: 'horarios',
      width: 150,
      render: (_, record) => {
        const horarios = record.horarios || [];
        const sinSku = horarios.filter(h => !h.sku).length;
        const skus = horarios.filter(h => h.sku).map(h => h.sku);
        return (
          <div className="flex flex-col items-start gap-1">
            <Tooltip title={skus.length ? `SKU: ${skus.join(' · ')}` : 'Sin SKU configurados'}>
              <button type="button" onClick={() => handleViewHorarios(record)}
                className="inline-flex items-center gap-1.5 text-sm font-bold text-primary hover:underline">
                <ClockCircleOutlined /> {horarios.length} horario{horarios.length !== 1 ? 's' : ''}
              </button>
            </Tooltip>
            {sinSku > 0 && (
              <Tooltip title="Estos horarios no tienen código SKU de Bsale: sus boletas no se pueden emitir.">
                <span className="px-2 py-px rounded-full bg-secondary/50 text-[#5c4300] text-[10px] font-bold cursor-help">{sinSku} sin SKU</span>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: <span className="sr-only">Acciones</span>,
      key: 'acciones',
      width: 56,
      align: 'center',
      render: (_, record) => {
        const cerrarDisabled = !record.enCicloActivo;
        const items = [
          { key: 'horarios', icon: <ClockCircleOutlined />, label: 'Ver horarios e inscritos', onClick: () => handleViewHorarios(record) },
          ...(isVentasOnly ? [] : [
            { key: 'editar', icon: <EditOutlined />, label: 'Editar', onClick: () => navigate(`/admin/talleres/editar/${record._id}`) },
            { key: 'historial', icon: <HistoryOutlined />, label: 'Historial de ciclos', onClick: () => handleVerHistorial(record) },
            { type: 'divider' },
            {
              key: 'cerrar', icon: <StopOutlined />, danger: !cerrarDisabled, disabled: cerrarDisabled,
              label: cerrarDisabled ? 'Cerrar ciclo (sin ciclo activo)' : 'Cerrar ciclo',
              onClick: () => !cerrarDisabled && handleCerrarCiclo(record),
            },
          ]),
        ];
        return (
          <Dropdown menu={{ items }} trigger={['click']} placement="bottomRight">
            <button type="button" aria-label={`Acciones de ${record.nombre}`}
              className="w-9 h-9 inline-flex items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors">
              <MoreOutlined style={{ fontSize: 16 }} />
            </button>
          </Dropdown>
        );
      },
    },
  ];


  const handleExportarExcel = async () => {
    if (!filteredData.length) return;
    try {
      const filas = filteredData.map(t => {
        const { cuposOcupados, cuposTotales } = getCuposStats(t);
        const disponibles = Math.max((cuposTotales || 0) - (cuposOcupados || 0), 0);
        const horarios = t.horarios || [];
        return {
          nombre: t.nombre,
          recinto: t.recinto?.nombre,
          estado: t.estado,
          inscripciones: t.inscripcionesEstado,
          cicloActual: t.cicloActual || 0,
          skus: horarios.map(h => h.sku).filter(Boolean).join(' · '),
          horariosSinSku: horarios.filter(h => !h.sku).length,
          cuposOcupados,
          cuposTotales,
          disponibles,
          // Cupos totales en 0 daría división por cero: sin cupos definidos la
          // ocupación no está definida, no es 0 %.
          ocupacion: cuposTotales > 0 ? (cuposOcupados / cuposTotales) * 100 : null,
        };
      });

      await exportarLibro({
        archivo: 'talleres',
        titulo: 'Talleres',
        subtitulo: 'Estado de talleres, ciclos y ocupación de cupos',
        usuario: user,
        filtros: {
          'Búsqueda': searchText || null,
          'Recinto': recintoFilter !== 'all' ? recintoFilter : 'Todos',
          'Estado': estadoFilter !== 'all' ? estadoFilter : 'Todos',
          'Inscripciones': inscFilter !== 'all' ? inscFilter : 'Todas',
        },
        hojas: [{
          nombre: 'Talleres',
          columnas: [
            { header: 'Nombre', key: 'nombre', tipo: 'texto', ancho: 38 },
            { header: 'Recinto', key: 'recinto', tipo: 'texto', ancho: 28 },
            {
              header: 'Estado', key: 'estado', tipo: 'texto',
              resaltar: (t) => (t.estado === 'activo' ? 'ok' : t.estado === 'inactivo' ? 'alerta' : null),
            },
            { header: 'Inscripciones', key: 'inscripciones', tipo: 'texto' },
            { header: 'Ciclo actual', key: 'cicloActual', tipo: 'numero' },
            { header: 'SKU de los horarios', key: 'skus', tipo: 'texto', ancho: 30 },
            {
              header: 'Horarios sin SKU', key: 'horariosSinSku', tipo: 'numero',
              resaltar: (t) => (t.horariosSinSku > 0 ? 'alerta' : null),
            },
            { header: 'Cupos ocupados', key: 'cuposOcupados', tipo: 'numero', total: true },
            { header: 'Cupos totales', key: 'cuposTotales', tipo: 'numero', total: true },
            { header: 'Disponibles', key: 'disponibles', tipo: 'numero', total: true },
            {
              header: '% ocupación', key: 'ocupacion', tipo: 'porcentaje',
              resaltar: (t) => (t.ocupacion >= 90 ? 'alerta' : t.ocupacion >= 70 ? 'aviso' : null),
            },
          ],
          filas,
          notas: ['% ocupación en blanco: el taller no tiene cupos configurados.'],
        }],
      });
    } catch { modal.error({ title: 'Error al generar Excel' }); }
  };

  const nSel = selectedRowKeys.length;
  const btnBarra = 'inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-bold transition-colors disabled:opacity-40 disabled:pointer-events-none';

  return (
    <div>
      <EncabezadoAdmin
        titulo={isVentasOnly ? 'Consulta de' : 'Gestión de'}
        destacado="talleres"
        subtitulo={isJefeRecinto
          ? `Vista de ${nombreRecintoDelJefe} · ${conteo.total} talleres`
          : isVentasOnly
            ? `${conteo.total} talleres · solo lectura, con descarga de inscritos por horario`
            : `${conteo.total} talleres · ${conteo.activos} activos`}
      >
        <button type="button" onClick={handleExportarExcel} disabled={!filteredData.length} className={claseBtnSecundario}>
          <DownloadOutlined /> Exportar Excel
        </button>
        {!isVentasOnly && (
          <button type="button" onClick={() => navigate('/admin/talleres/crear')} className={claseBtnPrimario}>
            <PlusOutlined /> Crear taller
          </button>
        )}
      </EncabezadoAdmin>

      {/* Resumen: cada tarjeta filtra. Las de atención se marcan en amarillo si hay casos. */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
        <TarjetaResumen icon={<AppstoreOutlined />} label="Todos" value={conteo.total}
          activa={tarjetaActiva === 'todos'} onClick={() => elegirTarjeta('todos')} />
        <TarjetaResumen icon={<PlayCircleOutlined />} label="Activos" value={conteo.activos}
          activa={tarjetaActiva === 'activos'} onClick={() => elegirTarjeta('activos')} />
        <TarjetaResumen icon={<PauseCircleOutlined />} label="Pausados o borrador" value={conteo.pausados}
          activa={tarjetaActiva === 'pausados'} onClick={() => elegirTarjeta('pausados')} />
        <TarjetaResumen icon={<ExclamationCircleOutlined />} label="Sin ciclo activo" value={conteo.sinCiclo}
          activa={tarjetaActiva === 'sinCiclo'} onClick={() => elegirTarjeta('sinCiclo')} />
        <TarjetaResumen icon={<TagOutlined />} label="Con horarios sin SKU" value={conteo.sinSku}
          activa={tarjetaActiva === 'sinSku'} onClick={() => elegirTarjeta('sinSku')} />
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-3 p-3 mb-4 rounded-2xl bg-white border border-gray-100">
        <Input
          allowClear
          prefix={<SearchOutlined className="text-gray-400" />}
          placeholder="Buscar por nombre, descripción o recinto…"
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          className="!rounded-full"
          style={{ maxWidth: 340 }}
          aria-label="Buscar talleres"
        />
        <Select value={recintoFilter} onChange={setRecintoFilter} style={{ minWidth: 190 }} disabled={isJefeRecinto}
          aria-label="Recinto" showSearch optionFilterProp="children">
          <Option value="all">Todos los recintos</Option>
          {recintos.sort((a, b) => a.localeCompare(b, 'es')).map(r => <Option key={r} value={r}>{r}</Option>)}
        </Select>
        <FiltroChips label="Inscripciones" valor={inscFilter} onChange={setInscFilter} opciones={[
          { value: 'all', label: 'Todas' },
          { value: 'abiertas', label: 'Abiertas' },
          { value: 'pausadas', label: 'Pausadas' },
        ]} />
        {hayFiltros && (
          <button type="button" onClick={limpiarFiltros} className="text-sm font-semibold text-gray-500 hover:text-primary">
            Limpiar filtros
          </button>
        )}
        <span className="ml-auto text-xs font-semibold text-gray-500">
          {filteredData.length} resultado{filteredData.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Acciones masivas: aparecen solo con selección. Antes "Nuevo ciclo" y
          "Cerrar ciclos" vivían siempre deshabilitados en el encabezado y el resto
          en la barra de filtros, con un color distinto por botón. */}
      {!isVentasOnly && nSel > 0 && (
        <div className="sticky top-2 z-20 flex flex-wrap items-center gap-2 p-2.5 pl-4 mb-3 rounded-2xl bg-[#00266b] text-white shadow-[0_12px_28px_-12px_rgba(0,38,107,.6)]">
          <span className="text-sm font-bold mr-1">
            <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 mr-2 rounded-full bg-secondary text-[#00266b] text-xs font-extrabold">{nSel}</span>
            seleccionado{nSel !== 1 ? 's' : ''}
          </span>
          <button type="button" onClick={abrirNuevoDesdeSeleccion} disabled={selectedForNuevo.length === 0}
            className={`${btnBarra} bg-secondary text-[#00266b] hover:brightness-105`}>
            <ClockCircleOutlined /> Nuevo ciclo ({selectedForNuevo.length})
          </button>
          <button type="button" onClick={handleBulkActivate} disabled={selectedPausados.length === 0 || loadingBulk}
            className={`${btnBarra} bg-white/10 hover:bg-white/20`}>
            <PlayCircleOutlined /> Activar ({selectedPausados.length})
          </button>
          <button type="button" onClick={handleBulkPause} disabled={selectedActivos.length === 0 || loadingBulk}
            className={`${btnBarra} bg-white/10 hover:bg-white/20`}>
            <PauseCircleOutlined /> Pausar ({selectedActivos.length})
          </button>
          <button type="button" onClick={handleBulkUpdateActivityDates} disabled={loadingBulk}
            className={`${btnBarra} bg-white/10 hover:bg-white/20`}>
            <CalendarOutlined /> Fechas de actividades
          </button>
          <button type="button" onClick={abrirCerrarMasivo} disabled={selectedForCerrar.length === 0}
            className={`${btnBarra} bg-white/10 hover:bg-red-500/80`}>
            <StopOutlined /> Cerrar ciclos ({selectedForCerrar.length})
          </button>
          <button type="button" onClick={() => setSelectedRowKeys([])}
            className="ml-auto px-3 py-2 text-xs font-semibold text-blue-100 hover:text-white">
            Quitar selección
          </button>
        </div>
      )}

      {/* ── Table ── */}
      <div className="admin-table-card">
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="_id"
          rowSelection={isVentasOnly ? undefined : rowSelection}
          sticky
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} talleres`,
            pageSizeOptions: ['10', '20', '50', '100'],
            style: { padding: '12px 16px' },
          }}
          scroll={{ x: 960 }}
          rowClassName={(record) => !record.enCicloActivo ? 'row-sin-ciclo' : ''}
          locale={{
            emptyText: (
              <div className="py-10 text-center">
                <p className="font-bold text-gray-900 m-0">No hay talleres con estos filtros</p>
                <p className="text-sm text-gray-500 m-0 mt-1">Prueba con otra búsqueda o limpia los filtros.</p>
              </div>
            ),
          }}
        />
      </div>

      {/* Modal: Cerrar ciclo (individual) */}
      <Modal
        open={cerrarCicloModalVisible}
        onCancel={() => { setCerrarCicloModalVisible(false); formCerrarIndividual.resetFields(); setTallerParaCerrarCiclo(null); }}
        width={520}
        destroyOnHidden
        title={<>Cerrar <span className="hl-amarillo">ciclo</span></>}
        footer={
          <div className="flex justify-end gap-2">
            <button type="button" className={claseBtnSecundario}
              onClick={() => { setCerrarCicloModalVisible(false); formCerrarIndividual.resetFields(); setTallerParaCerrarCiclo(null); }}>
              Volver
            </button>
            <button type="button" onClick={() => formCerrarIndividual.submit()} disabled={loadingCiclo} className={claseBtnPeligro}>
              {loadingCiclo ? <LoadingOutlined /> : <StopOutlined />} Cerrar ciclo {tallerParaCerrarCiclo?.cicloActual}
            </button>
          </div>
        }
      >
        <p className="text-sm text-gray-600 mb-4">
          <strong>{tallerParaCerrarCiclo?.nombre}</strong> · {tallerParaCerrarCiclo?.recinto?.nombre || 'Sin recinto'} · ciclo {tallerParaCerrarCiclo?.cicloActual}
        </p>
        <Alert type="info" showIcon className="mb-4"
          message="Se guarda una foto del ciclo"
          description="Quedan registrados los horarios, inscripciones y datos actuales del taller. Puedes verla después en el historial de ciclos." />
        <Form form={formCerrarIndividual} layout="vertical" onFinish={handleConfirmarCerrarCiclo} requiredMark={false}>
          <Form.Item label="Motivo del cierre" name="motivoCierre" initialValue="cierre_manual">
            <Select options={MOTIVOS_CIERRE} />
          </Form.Item>
          <Form.Item label="Notas (opcional)" name="notas" style={{ marginBottom: 0 }}>
            <TextArea rows={3} placeholder="Observaciones sobre el cierre del ciclo" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal: Iniciar nuevo ciclo */}
      <Modal
        open={iniciarCicloModalVisible}
        onCancel={() => { setIniciarCicloModalVisible(false); formNuevoCiclo.resetFields(); }}
        width={600}
        destroyOnHidden
        title={<>Iniciar <span className="hl-amarillo">nuevo ciclo</span></>}
        footer={
          <div className="flex justify-end gap-2">
            <button type="button" className={claseBtnSecundario} onClick={() => { setIniciarCicloModalVisible(false); formNuevoCiclo.resetFields(); }}>
              Volver
            </button>
            <button type="button" onClick={() => formNuevoCiclo.submit()} disabled={loadingCiclo} className={claseBtnPrimario}>
              {loadingCiclo ? <LoadingOutlined /> : <PlayCircleOutlined />}
              Iniciar ciclo{selectedForNuevo.length > 1 ? ` en ${selectedForNuevo.length} talleres` : ''}
            </button>
          </div>
        }
      >
        <p className="text-sm text-gray-600 mb-3">
          Se copian los horarios del ciclo anterior. Cada persona que se inscriba tendrá su propia suscripción desde el día que pague.
        </p>
        <ListaTalleres talleres={selectedForNuevo} detalle={t => `${t.horarios?.length || 0} horario${(t.horarios?.length || 0) !== 1 ? 's' : ''}`} />

        <Form form={formNuevoCiclo} layout="vertical" onFinish={handleConfirmarNuevoCiclo} className="mt-5" requiredMark={false}>
          <p className={claseTituloSeccion}>Ventana de inscripción · opcional</p>
          <p className="text-xs text-gray-500 mt-1 mb-3">
            Desde cuándo y hasta cuándo pueden inscribirse, con hora exacta (hora de Chile). Si la dejas vacía, abres y pausas las inscripciones a mano.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3">
            <Form.Item label="Abre" name="inscripcionInicio">
              <DatePicker showTime={{ format: 'HH:mm', minuteStep: 15, showNow: false }} format="DD/MM/YYYY HH:mm"
                style={{ width: '100%' }} placeholder="Fecha y hora de apertura" />
            </Form.Item>
            <Form.Item label="Cierra" name="inscripcionFin">
              <DatePicker showTime={{ format: 'HH:mm', minuteStep: 15, showNow: false, defaultValue: dayjs().hour(23).minute(59) }}
                format="DD/MM/YYYY HH:mm" style={{ width: '100%' }} placeholder="Fecha y hora de cierre" />
            </Form.Item>
          </div>
        </Form>
      </Modal>

      {/* Modal: Cerrar ciclos (masivo) */}
      <Modal
        open={cerrarMasivoVisible}
        onCancel={() => { setCerrarMasivoVisible(false); formCerrarMasivo.resetFields(); }}
        width={560}
        destroyOnHidden
        title={<>Cerrar <span className="hl-amarillo">{selectedForCerrar.length} ciclo{selectedForCerrar.length !== 1 ? 's' : ''}</span></>}
        footer={
          <div className="flex justify-end gap-2">
            <button type="button" className={claseBtnSecundario} onClick={() => { setCerrarMasivoVisible(false); formCerrarMasivo.resetFields(); }}>
              Volver
            </button>
            <button type="button" onClick={() => formCerrarMasivo.submit()} disabled={loadingCiclo} className={claseBtnPeligro}>
              {loadingCiclo ? <LoadingOutlined /> : <StopOutlined />} Cerrar {selectedForCerrar.length} ciclo{selectedForCerrar.length !== 1 ? 's' : ''}
            </button>
          </div>
        }
      >
        <Alert type="warning" showIcon className="mb-3"
          message="Se cierra el ciclo actual de cada taller"
          description="De cada uno queda guardada una foto de sus horarios e inscripciones." />
        <ListaTalleres talleres={selectedForCerrar} detalle={t => `Ciclo ${t.cicloActual}`} />
        <Form form={formCerrarMasivo} layout="vertical" onFinish={handleConfirmarCerrarMasivo} className="mt-4" requiredMark={false}>
          <Form.Item label="Motivo del cierre" name="motivoCierre" initialValue="cierre_manual">
            <Select options={MOTIVOS_CIERRE} />
          </Form.Item>
          <Form.Item label="Notas (opcional)" name="notas" style={{ marginBottom: 0 }}>
            <TextArea rows={3} placeholder="Observaciones para el cierre" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal: Actualizar fechas de actividades (masivo) */}
      <Modal
        open={actividadesFechasVisible}
        onCancel={() => { setActividadesFechasVisible(false); formActividadesFechas.resetFields(); }}
        width={600}
        destroyOnHidden
        title={<>Fechas de <span className="hl-amarillo">actividades</span></>}
        footer={
          <div className="flex justify-end gap-2">
            <button type="button" className={claseBtnSecundario} onClick={() => { setActividadesFechasVisible(false); formActividadesFechas.resetFields(); }}>
              Volver
            </button>
            <button type="button" onClick={() => formActividadesFechas.submit()} disabled={loadingBulk} className={claseBtnPrimario}>
              {loadingBulk ? <LoadingOutlined /> : <CalendarOutlined />}
              Aplicar a {selectedTalleres.length} taller{selectedTalleres.length !== 1 ? 'es' : ''}
            </button>
          </div>
        }
      >
        <p className="text-sm text-gray-600 mb-3">
          El período que elijas reemplaza las fechas de actividades de todos estos talleres. Es lo que las personas ven como vigencia de su inscripción.
        </p>
        <ListaTalleres talleres={selectedTalleres} />

        <Form form={formActividadesFechas} layout="vertical" onFinish={handleConfirmarActividadesFechas} className="mt-5" requiredMark={false}>
          <Form.Item
            label="Período de actividades (hora de Chile)"
            name="fechasActividades"
            rules={[{ required: true, message: 'Elige la fecha de inicio y la de fin' }]}
            extra="Debe empezar después del cierre de inscripciones de cada taller."
            style={{ marginBottom: 0 }}
          >
            <RangePicker showTime={{ format: 'HH:mm', minuteStep: 15 }} format="DD/MM/YYYY HH:mm"
              style={{ width: '100%' }} placeholder={['Inicio', 'Fin']} />
          </Form.Item>
        </Form>
      </Modal>

      <HorariosModal
        visible={horariosModalVisible}
        taller={selectedTaller}
        onClose={handleCloseModalHorarios}
        onEdit={tallerId => navigate(`/admin/talleres/editar/${tallerId}`)}
        onHorariosChange={handleHorariosChange}
      />

    </div>
  );
};

export default TalleresTable;
